import { Show, For, createSignal, createEffect, type Component } from "solid-js";
import { PanelLayout, type SubTab, type PanelStatus } from "@/shared/panel-base";
import { DataTable, type Column } from "@/shared/data-table";
import { createToolQuery } from "@/shared/query";
import { useToolClient } from "@/shared/tool-client";
import { useConfirm } from "@/shared/confirm-dialog";
import { useWs } from "@/shared/ws";
import { PanelBadge } from "@/shared/panel-primitives";
import { registerPanel } from "@/panel-registry";
import { Drawer } from "@/shared/drawer";
import "./user-admin.css";

interface LocalUser {
  Name?: string;
  Enabled?: boolean;
  FullName?: string;
  Description?: string;
  LastLogon?: string;
  PasswordExpires?: string | null;
}

interface LocalGroup {
  Name?: string;
  Description?: string;
  SID?: string;
  Members?: GroupMember[];
}

interface GroupMember {
  Name?: string;
  ObjectClass?: string;
  PrincipalSource?: string;
}

interface ListResult<T> {
  rows?: T[];
  users?: T[];
  groups?: T[];
  // L'agent C# sérialise le résultat en PascalCase (Users/Groups) — le
  // [JsonSourceGenerationOptions(CamelCase)] du plugin n'est pas appliqué au
  // résultat par le SDK. On accepte donc les deux casses.
  Users?: T[];
  Groups?: T[];
}

type UserAction = "enable" | "disable" | "reset-password";

const PASSWORD_RE = /^[\x21-\x7E]{8,128}$/;

function formatDate(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
    try {
      return new Date(v).toLocaleString();
    } catch {
      return v;
    }
  }
  return String(v);
}

function readString(row: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const value = row[key];
    if (value != null) return String(value);
  }
  return undefined;
}

function readBool(row: Record<string, unknown>, ...keys: string[]): boolean | undefined {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === "boolean") return value;
    if (typeof value === "string") return /true|enabled|actif/i.test(value);
  }
  return undefined;
}

function listRows<T>(result: ListResult<T> | undefined, key: "users" | "groups"): T[] {
  const pascalKey = key === "users" ? "Users" : "Groups";
  return result?.rows ?? result?.[key] ?? result?.[pascalKey] ?? [];
}

function normalizeUser(row: LocalUser & Record<string, unknown>): LocalUser {
  return {
    Name: readString(row, "Name", "name"),
    Enabled: readBool(row, "Enabled", "enabled"),
    FullName: readString(row, "FullName", "fullName"),
    Description: readString(row, "Description", "description"),
    LastLogon: readString(row, "LastLogon", "lastLogon"),
    PasswordExpires: readString(row, "PasswordExpires", "passwordExpires") ?? null,
  };
}

function normalizeMember(row: GroupMember & Record<string, unknown>): GroupMember {
  return {
    Name: readString(row, "Name", "name"),
    ObjectClass: readString(row, "ObjectClass", "objectClass"),
    PrincipalSource: readString(row, "PrincipalSource", "principalSource"),
  };
}

function normalizeGroup(row: LocalGroup & Record<string, unknown>): LocalGroup {
  const membersRaw = (row.Members ?? row["members"] ?? []) as GroupMember[];
  return {
    Name: readString(row, "Name", "name"),
    Description: readString(row, "Description", "description"),
    SID: readString(row, "SID", "sid"),
    Members: membersRaw.map((m) => normalizeMember(m as GroupMember & Record<string, unknown>)),
  };
}

const LocalUsersPanel: Component = () => {
  const ws = useWs();
  const confirm = useConfirm();
  const tools = useToolClient();
  const [filter, setFilter] = createSignal("");
  const [actionStatus, setActionStatus] = createSignal<PanelStatus | null>(null);
  const [members, setMembers] = createSignal<{ group: string; rows: GroupMember[] } | null>(null);
  // Drawer de réinitialisation de mot de passe local.
  const [pwdTarget, setPwdTarget] = createSignal<LocalUser | null>(null);
  // Drawer d'action de membre de groupe.
  const [memberDrawer, setMemberDrawer] = createSignal<{ group: string; mode: "add" | "remove" } | null>(null);
  // Sélection multiple (bulk).
  const [selected, setSelected] = createSignal<Record<string, boolean>>({});
  const [bulkProgress, setBulkProgress] = createSignal<{ done: number; total: number } | null>(null);

  const enabled = () => ws.state.nativeAgentOnline && ws.state.supportApproved;
  const pluginInstalled = () => Boolean(ws.state.pluginVersions["localusers"]);

  const usersQuery = createToolQuery<ListResult<LocalUser>>(() => ({
    tool: "localusers_list_users",
    params: {},
    enabled: enabled(),
    refetchInterval: 0,
  }));

  const groupsQuery = createToolQuery<ListResult<LocalGroup>>(() => ({
    tool: "localusers_list_groups",
    params: {},
    enabled: enabled(),
    refetchInterval: 0,
  }));

  function applyFilter<T>(rows: T[]): T[] {
    const q = filter().trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      for (const k of Object.keys(r as object)) {
        const v = (r as Record<string, unknown>)[k];
        if (v == null) continue;
        if (String(v).toLowerCase().includes(q)) return true;
      }
      return false;
    });
  }

  const visibleUsers = (): LocalUser[] =>
    applyFilter(listRows(usersQuery.data, "users").map((r) => normalizeUser(r as LocalUser & Record<string, unknown>)));
  const visibleGroups = (): LocalGroup[] =>
    applyFilter(
      listRows(groupsQuery.data, "groups").map((r) => normalizeGroup(r as LocalGroup & Record<string, unknown>)),
    );
  const selectedRows = (): LocalUser[] => visibleUsers().filter((r) => !!selected()[String(r.Name || "")]);

  function setRowSelected(name: string, on: boolean): void {
    if (!name) return;
    setSelected((prev) => {
      const next = { ...prev };
      if (on) next[name] = true;
      else delete next[name];
      return next;
    });
  }

  function clearSelection(): void {
    setSelected({});
  }

  function toggleAll(on: boolean): void {
    if (!on) {
      clearSelection();
      return;
    }
    const next: Record<string, boolean> = {};
    for (const r of visibleUsers()) {
      const n = String(r.Name || "");
      if (n) next[n] = true;
    }
    setSelected(next);
  }

  /** Appelle l'outil canonique localusers_* ; retourne true si succès. */
  async function callUserAction(name: string, action: UserAction, newPassword?: string): Promise<boolean> {
    if (!name) return false;
    const params: Record<string, unknown> = { userName: name };
    if (newPassword) params.password = newPassword;
    const tool =
      action === "enable"
        ? "localusers_enable_user"
        : action === "disable"
          ? "localusers_disable_user"
          : "localusers_reset_password";
    try {
      await tools.call(tool, params, { userConfirmedAt: new Date().toISOString() });
      return true;
    } catch (err) {
      setActionStatus({
        text: `Erreur: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
      return false;
    }
  }

  async function runUserAction(name: string, action: Exclude<UserAction, "reset-password">): Promise<void> {
    if (!name) return;
    const labels: Record<typeof action, string> = {
      enable: "Enable",
      disable: "Disable",
    };
    const levels: Record<typeof action, "info" | "warn" | "danger"> = {
      enable: "info",
      disable: "warn",
    };
    const label = labels[action];
    const level = levels[action];
    const ok = await confirm.confirm({
      title: `${label} - ${name}`,
      body: `Confirmer l'action "${label}" sur l'utilisateur local ${name} ?`,
      level,
      confirmLabel: label,
      entityName: level === "danger" ? name : "",
    });
    if (!ok) return;
    setActionStatus({ text: `Execution ${label} sur ${name}...`, level: "info" });
    const success = await callUserAction(name, action);
    if (success) {
      setActionStatus({ text: `Action ${label} sur ${name} reussie.`, level: "info" });
      await usersQuery.refetch();
    }
  }

  /** Action de masse : enable ou disable des comptes sélectionnés. */
  async function bulkAction(action: "enable" | "disable"): Promise<void> {
    if (!enabled()) return;
    const label = action === "enable" ? "Enable" : "Disable";
    const targets = selectedRows().filter((r) => (action === "enable" ? r.Enabled !== true : r.Enabled !== false));
    if (targets.length === 0) {
      setActionStatus({ text: "Aucun compte concerné par cette action.", level: "warn" });
      return;
    }
    const ok = await confirm.confirm({
      title: `${label} ${targets.length} compte${targets.length > 1 ? "s" : ""}`,
      body: `Confirmer l'action "${label}" en masse sur ${targets.length} utilisateur${targets.length > 1 ? "s" : ""} local ?`,
      level: action === "disable" ? "warn" : "info",
      confirmLabel: label,
    });
    if (!ok) return;
    setBulkProgress({ done: 0, total: targets.length });
    let failures = 0;
    for (let i = 0; i < targets.length; i++) {
      const success = await callUserAction(String(targets[i]!.Name || ""), action);
      if (!success) failures++;
      setBulkProgress({ done: i + 1, total: targets.length });
    }
    setBulkProgress(null);
    clearSelection();
    await usersQuery.refetch();
    setActionStatus(
      failures
        ? { text: `${label} terminé — ${failures} échec(s) sur ${targets.length}.`, level: "danger" }
        : { text: `${label} : ${targets.length} compte(s) traité(s).`, level: "info" },
    );
  }

  async function loadMembers(groupName: string): Promise<void> {
    if (!enabled()) return;
    if (!groupName) return;
    setActionStatus({ text: `Chargement membres ${groupName}...`, level: "info" });
    try {
      const r = (await tools.call<{ rows?: GroupMember[]; Rows?: GroupMember[] }>("localusers_group_members", {
        groupName,
      })) as { rows?: GroupMember[]; Rows?: GroupMember[] } | undefined;
      const rows = (r?.rows ?? r?.Rows ?? []).map((m) => normalizeMember(m as GroupMember & Record<string, unknown>));
      setMembers({ group: groupName, rows });
      setActionStatus({
        text: `${rows.length} membre${rows.length > 1 ? "s" : ""}.`,
        level: rows.length ? "info" : "warn",
      });
    } catch (err) {
      setActionStatus({
        text: `Erreur membres: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    }
  }

  /** Exécute l'ajout/retrait d'un membre saisi via le drawer. */
  async function submitMemberAction(groupName: string, mode: "add" | "remove", userName: string): Promise<boolean> {
    if (!enabled()) return false;
    const trimmed = userName.trim();
    if (!trimmed) return false;
    const isAdd = mode === "add";
    const ok = await confirm.confirm({
      title: `${isAdd ? "Ajouter" : "Retirer"} ${trimmed}`,
      body: `Confirmer ${isAdd ? "l'ajout de" : "le retrait de"} ${trimmed} ${isAdd ? "au" : "du"} groupe ${groupName} ?`,
      level: isAdd ? "warn" : "danger",
      confirmLabel: isAdd ? "Ajouter" : "Retirer",
      entityName: isAdd ? "" : trimmed,
    });
    if (!ok) return false;
    setActionStatus({ text: `Execution ${mode} ${trimmed} sur ${groupName}...`, level: "info" });
    try {
      const tool = isAdd ? "localusers_add_to_group" : "localusers_remove_from_group";
      await tools.call(tool, { groupName, userName: trimmed }, { userConfirmedAt: new Date().toISOString() });
      setActionStatus({ text: `Action ${mode} sur ${trimmed} reussie.`, level: "info" });
      const cur = members();
      if (cur && cur.group === groupName) await loadMembers(groupName);
      return true;
    } catch (err) {
      setActionStatus({
        text: `Erreur: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
      return false;
    }
  }

  const userColumns: Column<LocalUser>[] = [
    {
      id: "_select",
      enableSorting: false,
      header: () => (
        <span class="ua-check">
          <input
            type="checkbox"
            checked={visibleUsers().length > 0 && visibleUsers().every((r) => !!selected()[String(r.Name || "")])}
            title="Tout sélectionner"
            onChange={(e) => toggleAll(e.currentTarget.checked)}
          />
        </span>
      ),
      cell: (info) => {
        const name = String(info.row.original.Name || "");
        return (
          <span class="ua-check">
            <input
              type="checkbox"
              checked={!!selected()[name]}
              onChange={(e) => setRowSelected(name, e.currentTarget.checked)}
            />
          </span>
        );
      },
    },
    { accessorKey: "Name", header: "Name" },
    {
      accessorKey: "Enabled",
      header: "Enabled",
      cell: (info) => {
        const v = !!info.getValue();
        return <PanelBadge intent={v ? "ok" : "danger"}>{v ? "Enabled" : "Disabled"}</PanelBadge>;
      },
    },
    { accessorKey: "FullName", header: "FullName" },
    { accessorKey: "Description", header: "Description" },
    {
      accessorKey: "LastLogon",
      header: "LastLogon",
      cell: (info) => formatDate(info.getValue()),
    },
    {
      accessorKey: "PasswordExpires",
      header: "PasswordExpires",
      cell: (info) => {
        const v = info.getValue();
        return (
          <Show when={v} fallback={<PanelBadge intent="ok">Never</PanelBadge>}>
            {(expiresAt) => <PanelBadge intent="warn">{formatDate(expiresAt())}</PanelBadge>}
          </Show>
        );
      },
    },
  ];

  const groupColumns: Column<LocalGroup>[] = [
    { accessorKey: "Name", header: "Name" },
    { accessorKey: "Description", header: "Description" },
    { accessorKey: "SID", header: "SID" },
  ];

  const toolbar = (
    <>
      <input
        type="text"
        class="panel-filter-input"
        placeholder="Filtre (nom partiel)..."
        spellcheck={false}
        value={filter()}
        onInput={(e) => setFilter(e.currentTarget.value)}
      />
      <button
        type="button"
        class="btn btn--xs btn--info"
        disabled={!enabled()}
        onClick={() => {
          usersQuery.refetch();
          groupsQuery.refetch();
        }}
      >
        Rafraichir
      </button>
    </>
  );

  const subtabs: SubTab[] = [
    {
      id: "users",
      label: "Utilisateurs",
      render: () => (
        <>
          <Show when={selectedRows().length > 0}>
            <div class="ua-bulkbar">
              <span class="ua-bulkbar__count">{selectedRows().length} sélectionné(s)</span>
              <button
                type="button"
                class="btn btn--xs btn--ghost"
                disabled={!enabled() || !!bulkProgress()}
                onClick={() => bulkAction("enable")}
              >
                Enable
              </button>
              <button
                type="button"
                class="btn btn--xs btn--warn"
                disabled={!enabled() || !!bulkProgress()}
                onClick={() => bulkAction("disable")}
              >
                Disable
              </button>
              <span class="ua-bulkbar__spacer" />
              <button type="button" class="btn btn--xs btn--ghost" onClick={clearSelection}>
                Effacer
              </button>
              <Show when={bulkProgress()}>
                {(p) => (
                  <>
                    <div class="ua-bulkbar__progress">
                      <div
                        class="ua-bulkbar__progress-fill"
                        style={{ width: `${(p().done / Math.max(1, p().total)) * 100}%` }}
                      />
                    </div>
                    <span class="ua-bulkbar__progress-label">
                      {p().done} / {p().total}
                    </span>
                  </>
                )}
              </Show>
            </div>
          </Show>
          <DataTable<LocalUser>
            rows={visibleUsers()}
            columns={userColumns}
            rowActions={(r) => {
              const name = String(r.Name || "");
              return (
                <>
                  <button
                    type="button"
                    class="btn btn--xs btn--ghost"
                    disabled={!enabled() || r.Enabled === true}
                    onClick={() => runUserAction(name, "enable")}
                  >
                    Enable
                  </button>
                  <button
                    type="button"
                    class="btn btn--xs btn--warn"
                    disabled={!enabled() || r.Enabled === false}
                    onClick={() => runUserAction(name, "disable")}
                  >
                    Disable
                  </button>
                  <button
                    type="button"
                    class="btn btn--xs btn--danger"
                    disabled={!enabled()}
                    onClick={() => setPwdTarget(r)}
                  >
                    Reset-pwd
                  </button>
                </>
              );
            }}
          />
        </>
      ),
    },
    {
      id: "groups",
      label: "Groupes",
      render: () => (
        <>
          <DataTable<LocalGroup>
            rows={visibleGroups()}
            columns={groupColumns}
            rowActions={(r) => {
              const name = String(r.Name || "");
              return (
                <>
                  <button
                    type="button"
                    class="btn btn--xs btn--info"
                    disabled={!enabled()}
                    onClick={() => loadMembers(name)}
                  >
                    Voir membres
                  </button>
                  <button
                    type="button"
                    class="btn btn--xs btn--ghost"
                    disabled={!enabled()}
                    onClick={() => setMemberDrawer({ group: name, mode: "add" })}
                  >
                    Ajouter membre
                  </button>
                  <button
                    type="button"
                    class="btn btn--xs btn--warn"
                    disabled={!enabled()}
                    onClick={() => setMemberDrawer({ group: name, mode: "remove" })}
                  >
                    Retirer membre
                  </button>
                </>
              );
            }}
          />
          <Show when={members()}>
            {(m) => (
              <div class="panel-data-wrap" style={{ "margin-top": "12px" }}>
                <div class="panel-section-header">
                  <span class="eyebrow">Membres de {m().group}</span>
                  <button type="button" class="btn btn--xs btn--ghost" onClick={() => setMembers(null)}>
                    Fermer
                  </button>
                </div>
                <DataTable<GroupMember>
                  rows={m().rows}
                  columns={[
                    { accessorKey: "Name", header: "Name" },
                    { accessorKey: "ObjectClass", header: "ObjectClass" },
                    { accessorKey: "PrincipalSource", header: "PrincipalSource" },
                  ]}
                  emptyMessage="Aucun membre."
                />
              </div>
            )}
          </Show>
        </>
      ),
    },
  ];

  const statusBar = (): PanelStatus | undefined => {
    if (actionStatus()) return actionStatus() ?? undefined;
    if (!ws.state.nativeAgentOnline) return { text: "Module natif requis.", level: "warn" };
    if (!ws.state.supportApproved) return { text: "Validation client requise.", level: "warn" };
    if (!pluginInstalled()) return { text: "Plugin LocalUsers non installé sur ce host.", level: "warn" };
    if (usersQuery.isFetching) return { text: "Chargement...", level: "info" };
    if (usersQuery.error)
      return {
        text: `Erreur: ${usersQuery.error instanceof Error ? usersQuery.error.message : String(usersQuery.error)}`,
        level: "danger",
      };
    return undefined;
  };

  return (
    <>
      <PanelLayout title="Comptes locaux" subtabs={subtabs} toolbar={toolbar} status={statusBar()} />
      <ResetPwdDrawer
        user={pwdTarget()}
        onClose={() => setPwdTarget(null)}
        onDone={(msg, level) => {
          setActionStatus({ text: msg, level });
          if (level !== "danger") void usersQuery.refetch();
        }}
        confirmFn={(name) =>
          confirm.confirm({
            title: `Reset-pwd - ${name}`,
            body: `Confirmer la réinitialisation du mot de passe de l'utilisateur local ${name} ?`,
            level: "danger",
            confirmLabel: "Reset-pwd",
            entityName: name,
          })
        }
        runFn={(name, pwd) => callUserAction(name, "reset-password", pwd)}
      />
      <MemberDrawer request={memberDrawer()} onClose={() => setMemberDrawer(null)} submitFn={submitMemberAction} />
    </>
  );
};

/** Drawer de réinitialisation de mot de passe local. */
function ResetPwdDrawer(props: {
  user: LocalUser | null;
  onClose: () => void;
  onDone: (msg: string, level: "info" | "warn" | "danger") => void | Promise<void>;
  confirmFn: (name: string) => Promise<boolean>;
  runFn: (name: string, pwd: string) => Promise<boolean>;
}) {
  const [pwd, setPwd] = createSignal("");
  const [error, setError] = createSignal<string | null>(null);
  const [busy, setBusy] = createSignal(false);

  const name = (): string => String(props.user?.Name || "");

  // Réinitialise le formulaire à chaque ouverture/changement de compte.
  createEffect(() => {
    void props.user;
    setPwd("");
    setError(null);
  });

  async function submit(): Promise<void> {
    setError(null);
    const value = pwd();
    if (!PASSWORD_RE.test(value)) {
      setError("Mot de passe invalide (8-128 caractères ASCII imprimables).");
      return;
    }
    const ok = await props.confirmFn(name());
    if (!ok) return;
    setBusy(true);
    void props.onDone(`Réinitialisation du mot de passe de ${name()}...`, "info");
    const success = await props.runFn(name(), value);
    setBusy(false);
    if (success) {
      void props.onDone(`Mot de passe de ${name()} réinitialisé.`, "info");
      props.onClose();
    } else {
      setError("Échec de la réinitialisation.");
    }
  }

  return (
    <Drawer open={!!props.user} onClose={props.onClose} title="Réinitialiser le mot de passe" width="420px">
      <Show when={props.user}>
        {(u) => (
          <div class="ua-form">
            <div class="ua-props">
              <h3 class="ua-props__title">Compte concerné</h3>
              <div class="ua-props__grid">
                <For
                  each={
                    [
                      ["Nom", u().Name || "—"],
                      ["Nom complet", u().FullName || "—"],
                      ["Description", u().Description || "—"],
                      ["Activé", u().Enabled ? "Oui" : "Non"],
                    ] as [string, string][]
                  }
                >
                  {([k, v]) => (
                    <>
                      <span class="ua-props__key">{k}</span>
                      <span class="ua-props__val">{v}</span>
                    </>
                  )}
                </For>
              </div>
            </div>
            <div class="ua-form__field">
              <span class="ua-form__label">Nouveau mot de passe</span>
              <input
                class="ua-inline__input"
                type="password"
                autocomplete="new-password"
                placeholder="8-128 caractères ASCII"
                value={pwd()}
                onInput={(e) => setPwd(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void submit();
                }}
              />
            </div>
            <Show when={error()}>
              <div class="ua-form__error">{error()}</div>
            </Show>
            <div class="ua-form__actions">
              <button type="button" class="btn btn--sm btn--ghost" onClick={props.onClose}>
                Annuler
              </button>
              <button type="button" class="btn btn--sm btn--danger" disabled={busy()} onClick={submit}>
                {busy() ? "Application..." : "Réinitialiser"}
              </button>
            </div>
          </div>
        )}
      </Show>
    </Drawer>
  );
}

/** Drawer d'ajout / retrait de membre d'un groupe local. */
function MemberDrawer(props: {
  request: { group: string; mode: "add" | "remove" } | null;
  onClose: () => void;
  submitFn: (group: string, mode: "add" | "remove", userName: string) => Promise<boolean>;
}) {
  const [value, setValue] = createSignal("");
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  // Réinitialise le formulaire à chaque ouverture/changement de requête.
  createEffect(() => {
    void props.request;
    setValue("");
    setError(null);
  });

  const isAdd = (): boolean => props.request?.mode === "add";

  async function submit(): Promise<void> {
    setError(null);
    const req = props.request;
    if (!req) return;
    if (!value().trim()) {
      setError("Nom de membre requis.");
      return;
    }
    setBusy(true);
    const ok = await props.submitFn(req.group, req.mode, value());
    setBusy(false);
    if (ok) props.onClose();
  }

  return (
    <Drawer
      open={!!props.request}
      onClose={props.onClose}
      title={isAdd() ? "Ajouter un membre" : "Retirer un membre"}
      width="420px"
    >
      <Show when={props.request}>
        {(req) => (
          <div class="ua-form">
            <div class="ua-props">
              <h3 class="ua-props__title">Groupe concerné</h3>
              <div class="ua-props__grid">
                <span class="ua-props__key">Groupe</span>
                <span class="ua-props__val">{req().group}</span>
                <span class="ua-props__key">Action</span>
                <span class="ua-props__val">{isAdd() ? "Ajout d'un membre" : "Retrait d'un membre"}</span>
              </div>
            </div>
            <div class="ua-form__field">
              <span class="ua-form__label">Membre</span>
              <input
                class="ua-inline__input"
                type="text"
                placeholder="ex: Alice ou DOMAIN\user"
                value={value()}
                onInput={(e) => setValue(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void submit();
                }}
              />
              <span class="ua-form__hint">Compte local ou compte de domaine au format DOMAINE\utilisateur.</span>
            </div>
            <Show when={error()}>
              <div class="ua-form__error">{error()}</div>
            </Show>
            <div class="ua-form__actions">
              <button type="button" class="btn btn--sm btn--ghost" onClick={props.onClose}>
                Annuler
              </button>
              <button
                type="button"
                class={`btn btn--sm ${isAdd() ? "btn--ghost" : "btn--warn"}`}
                disabled={busy()}
                onClick={submit}
              >
                {busy() ? "Application..." : isAdd() ? "Ajouter" : "Retirer"}
              </button>
            </div>
          </div>
        )}
      </Show>
    </Drawer>
  );
}

registerPanel("localusers", LocalUsersPanel);
export default LocalUsersPanel;
